import { Controller } from "stimulus"

// HLI-specific Donation Flow UI: WELLBY impact calculator, charity split, HLI's research tip, and
// the one_time/recurrent in-page toggle. Generic concerns (currency/amount, address requirements,
// UK Gift Aid, the core review summary) live in donation_flow_shared_controller.js -- see
// sharedController below.
export default class extends Controller {
  static targets = [
    "freqToggleButton", "variantField", "impactCard",
    "summaryCharitySplits", "summaryImpactRow", "summaryImpact", "summarySwitchLink", "summaryFrequency"
  ]

  static values = {
    variant: String,
    minimumContributionAmountOneTime: Number,
    minimumContributionAmountRecurrent: Number
  }

  initialize() {
    this.element.addEventListener("donation-wizard:pageShown", this.handlePageShown.bind(this))
    this.element.addEventListener("donation-wizard:validate", this.handleWizardValidate.bind(this))
    // donation_flow_shared_controller.js fires this on every amount edit and currency switch
    // (manualAmountEntered/selectPresetAmount/currencySelected all route through updateAmountCents).
    this.element.addEventListener("donation-flow-shared:amountChanged", () => this.updateImpactCard())
    this.updateFrequencyDisplay()
    // Deferred: wellby-impact-calculator/charity-split are nested inside this controller's own
    // element and connect after it during the initial Stimulus scan, so they aren't reachable synchronously here.
    setTimeout(() => this.updateImpactCard(), 0)
  }

  get sharedController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "donation-flow-shared")
  }

  get wizardController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "donation-wizard")
  }

  get donationFlowPaymentController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "donation-flow-payment")
  }

  // The client's minimum (Flows::Bricks::CaptureContributionAmount enforces it server-side) can
  // differ per variant -- surfaced on the "How much" step, not after "Donate now". The shared
  // controller's own minimumContributionAmountValue is fixed at page load, which isn't enough once
  // the in-page toggle can flip variant without a reload.
  get minimumContributionAmount() {
    return (this.variantValue === "one_time" ? this.minimumContributionAmountOneTimeValue : this.minimumContributionAmountRecurrentValue) || 0
  }

  handleWizardValidate(event) {
    const shared = this.sharedController
    if (event.detail.pageId === "amount" && shared && !this.validateAmount(shared)) {
      event.preventDefault()
    }
    if (event.detail.pageId === "personal_info" && shared && !shared.validatePostcode()) {
      event.preventDefault()
    }
  }

  // Same check as donation_flow_shared_controller#validateAmount, but against this controller's
  // own toggle-aware minimum instead of the shared controller's fixed one.
  validateAmount(shared) {
    if (!shared.hasAmountDollarsTarget) return true

    const minDollars = this.minimumContributionAmount
    const dollars = shared.amountDollars
    const symbol = shared.hasAmountCurrencySymbolTarget ? shared.amountCurrencySymbolTarget.textContent : ""

    return shared.markFieldValidity(
      shared.amountDollarsTarget,
      shared.hasAmountNoteTarget ? shared.amountNoteTarget : null,
      !isNaN(dollars) && dollars >= minDollars,
      shared.amountError(`The minimum donation is ${symbol}${minDollars}.`)
    )
  }

  handlePageShown(event) {
    if (event.detail.pageId === "review") {
      // Refresh the tip card first -- it may show/hide or reset its amount, and the summary below needs that final tip amount for the Tip row/Total charge.
      this.hliTipController?.refresh()
      this.sharedController?.updateReviewSummaryCore()
      this.updateReviewSummaryExtras()
    }
    if (event.detail.pageId === "amount") this.updateImpactCard()
  }

  // Purely a client-side state flip -- no navigation, no reload, so nothing about the amount, any
  // charity split, or Your details needs to be stashed/restored across a toggle anymore.
  selectVariant(event) {
    const newVariant = event.currentTarget.dataset.variant
    if (!newVariant || newVariant === this.variantValue) return

    this.variantValue = newVariant
    if (this.hasVariantFieldTarget) this.variantFieldTarget.value = newVariant
    this.freqToggleButtonTargets.forEach((btn) => btn.classList.toggle("is-active", btn.dataset.variant === newVariant))
    this.relabelPresetAmounts()
    this.updateFrequencyDisplay()
    // Not shared.validateAmount() -- that checks the shared controller's own fixed page-load
    // minimum, not this toggle's newly-selected variant's minimum (see #validateAmount above).
    const shared = this.sharedController
    if (shared) this.validateAmount(shared)
    shared?.updateReviewSummaryCore()
    this.updateReviewSummaryExtras()
    // Both the Amount step's own impact card and Allocate's (nested in charity-split) share this variant.
    this.impactCalculatorControllers.forEach((controller) => { controller.variantValue = newVariant })

    const tipController = this.hliTipController
    if (tipController) {
      tipController.variantValue = newVariant
      tipController.refresh?.()
    }

    const donationFlowPayment = this.donationFlowPaymentController
    if (donationFlowPayment) {
      donationFlowPayment.variantValue = newVariant
      // queueSetupPaymentElement shares the tip-edit debounce so a tip change + variant toggle on
      // Review cannot overlap two Customer+Intent creates (and reuse one Turnstile token).
      if (this.wizardController?.currentPageId === "review") donationFlowPayment.queueSetupPaymentElement(0)
    }
  }

  // Preset amounts differ per variant -- relabel the existing buttons from the amount step's
  // data-amounts-* payload instead of re-rendering. The donor's actual amount is left untouched;
  // only which preset (if any) shows as active is recomputed against the new labels.
  relabelPresetAmounts() {
    const shared = this.sharedController
    if (!shared || !shared.hasPresetAmountGroupTarget || !shared.hasPresetAmountTarget) return

    const key = this.variantValue === "one_time" ? "amountsOneTime" : "amountsRecurrent"
    const amounts = JSON.parse(shared.presetAmountGroupTarget.dataset[key] || "[]")
    const currentValue = shared.hasAmountDollarsTarget ? shared.amountDollarsTarget.value : null

    shared.presetAmountTargets.forEach((btn, i) => {
      if (amounts[i] == null) return
      btn.dataset.amount = amounts[i]
      btn.textContent = amounts[i]
      btn.classList.toggle("is-active", String(amounts[i]) === currentValue)
    })
  }

  updateFrequencyDisplay() {
    const isOneTime = this.variantValue === "one_time"
    if (this.hasSummaryFrequencyTarget) this.summaryFrequencyTarget.textContent = isOneTime ? "One-time" : "Monthly"
    if (this.hasSummarySwitchLinkTarget) {
      this.summarySwitchLinkTarget.textContent = isOneTime ? "Switch to monthly" : "Switch to one-time"
      this.summarySwitchLinkTarget.dataset.variant = isOneTime ? "recurrent" : "one_time"
    }
  }

  // The charity-split/impact rows this controller owns on top of the shared core summary.
  updateReviewSummaryExtras() {
    if (this.hasSummaryCharitySplitsTarget) {
      const controller = this.charitySplitController
      const rows = controller?.splitFractions || []
      this.summaryCharitySplitsTarget.innerHTML = rows.map(({ charity, fraction }) => `
        <p class="donation-flow__summary-row">
          <span class="donation-flow__summary-label">${charity.name}</span>
          <span class="donation-flow__summary-value">${Math.round(fraction * 100)}%</span>
        </p>
      `).join("")
    }
    if (this.hasSummaryImpactRowTarget) {
      const controller = this.activeImpactCalculatorController
      const depressionYears = controller ? controller.monthlyDepressionYears : 0
      const show = depressionYears > 0
      this.summaryImpactRowTarget.classList.toggle("is-hidden", !show)
      if (show && this.hasSummaryImpactTarget) {
        const suffix = this.variantValue === "one_time" ? "" : " per month"
        this.summaryImpactTarget.textContent = `${controller.formatNumber(depressionYears)} yrs depression averted${suffix}`
      }
    }
  }

  get impactCalculatorController() {
    return this.hasImpactCardTarget
      ? this.application.getControllerForElementAndIdentifier(this.impactCardTarget, "wellby-impact-calculator")
      : null
  }

  // Every wellby-impact-calculator instance on the page (Amount step's preview, Allocate's live split
  // calculator) -- used to keep them all in sync with a variant flip from the freq toggle.
  get impactCalculatorControllers() {
    return Array.from(this.element.querySelectorAll('[data-controller~="wellby-impact-calculator"]'))
      .map((element) => this.application.getControllerForElementAndIdentifier(element, "wellby-impact-calculator"))
      .filter(Boolean)
  }

  // The "allocate" fieldset (Recommended Charities only) stays in the DOM even when the wizard skips it for the selected portfolio
  get charitySplitController() {
    const element = this.element.querySelector('[data-controller~="charity-split"]')
    return element ? this.application.getControllerForElementAndIdentifier(element, "charity-split") : null
  }

  // "Contribution to HLI's research" tip card, on the Review step.
  get hliTipController() {
    const element = this.element.querySelector('[data-controller~="hli-tip"]')
    return element ? this.application.getControllerForElementAndIdentifier(element, "hli-tip") : null
  }

  // Whichever wellby-impact-calculator instance matches the donor's current pick: the Allocate step's live split calculator for Recommended Charities, or HLF's Amount-step preview.
  get activeImpactCalculatorController() {
    const splitController = this.charitySplitController
    if (splitController && splitController.addedEins.length > 0) {
      const element = this.element.querySelector('fieldset[data-page-id="allocate"] [data-controller~="wellby-impact-calculator"]')
      return element ? this.application.getControllerForElementAndIdentifier(element, "wellby-impact-calculator") : null
    }

    return this.isImpactTargetPortfolioSelected ? this.impactCalculatorController : null
  }

  // If the donor's current portfolio pick is the one impact_calculator is configured for
  get isImpactTargetPortfolioSelected() {
    const controller = this.impactCalculatorController
    if (!controller) return false

    const selectedPortfolioId = this.element.querySelector('[name="donation_form[managed_portfolio_id]"]')?.value
    return selectedPortfolioId === controller.configValue.portfolio_id
  }

  updateImpactCard() {
    const controller = this.impactCalculatorController
    if (!controller) return

    const isTargetPortfolio = this.isImpactTargetPortfolioSelected
    this.impactCardTarget.classList.toggle("is-hidden", !isTargetPortfolio)
    if (!isTargetPortfolio) return

    const shared = this.sharedController
    controller.amountCentsValue = shared?.hasAmountCentsTarget ? parseInt(shared.amountCentsTarget.value, 10) || 0 : 0
    // Stimulus only fires amountCentsValueChanged when the number itself changes, but switching
    // currency (e.g. USD -> GBP, both non-zero-decimal) often leaves amountCents numerically
    // identical -- yet the calculator's unitsPerUsd/isZeroDecimal (read live off the shared
    // amountCurrencySymbol element, not a Stimulus value) has changed underneath it. Re-render
    // unconditionally so a currency switch alone always refreshes the estimate.
    controller.render()
  }
}
